import React from 'react';
import PropTypes from 'prop-types';
import { Label, MultiColumnList, NoValue } from '@folio/stripes-components';
import { FormattedMessage } from 'react-intl';
import { map } from 'lodash';
import useData from '../../../../hooks/useData';
import CustomTitleFieldView from './CustomTitleFieldView';
import InstanceRelationshipFieldView from './InstanceRelationshipFieldView';
import {
  getCustomFieldValue,
  UnParseClassifications,
} from '../../../../utils/utils';

const noValue = <NoValue />;

const getDefaultSchemaFieldValue = (name, data) => {
  if (name === 'classifications') {
    // return data?.customFields?.classifications || data[name];
    return (
      data?.customFields?.classifications || UnParseClassifications(data[name])
    );
  }

  return data[name] || [{}];
};

const fieldArrayExpFieldsTypes = ['customTitle', 'instanceField'];
const containsAny = (arr1, arr2) => {
  return arr1.some((item) => arr2.includes(item));
};

const fieldArrayViewComponents = {
  customTitle: CustomTitleFieldView,
  instanceField: InstanceRelationshipFieldView,
};

function FieldArrayView({ fieldValues, locale, data }) {
  const dataSource = useData();

  const { name, label, schema, fieldArray } = fieldValues;
  const fieldArrayTypes = map(fieldArray, 'type');
  const fieldLabel = label.find((field) => field.locale === locale)?.value;
  const fieldValue =
    schema === 'default'
      ? getDefaultSchemaFieldValue(name, data)
      : getCustomFieldValue(name, data) || [];

  const visibleColumns = map(fieldArray, 'name');

  const getColumnMapping = () => {
    const columnMapping = {};

    fieldArray.forEach((field) => {
      columnMapping[field.name] = field.label?.find(
        (loc) => loc.locale === locale,
      )?.value;
    });
    return columnMapping;
  };

  const getItemValue = (item, field) => {
    const src = dataSource[field.dataOptionsSource];
    const value = src?.find((source) => source.id === item[field.name])?.name;
    return (
      <>
        {value ? (
          <FormattedMessage
            id={`ui-inventory.${field.dataOptionsSource}.name.${value}`}
            defaultMessage={value}
          />
        ) : (
          <NoValue />
        )}
      </>
    );
  };

  const getFormatter = () => {
    const formatter = {};

    fieldArray.forEach((field) => {
      if (field.type === 'select') {
        formatter[field.name] = (item) => getItemValue(item, field) || noValue;
      } else if (field.type === 'checkbox' || field.type === 'toggleButton') {
        formatter[field.name] = (item) => {
          return item[field.name] ? (
            <FormattedMessage
              id={`ui-plugin-inventory-templates.inventoryTemplates.${(item[field.name]).toString()}`}
              defaultMessage={(item[field.name])?.toString()}
            />
          ) : (
            noValue
          );
        };
      } else {
        formatter[field.name] = (item) => item[field.name] || noValue;
      }
    });

    return formatter;
  };

  const getColumnWidths = () => {
    const columnWidths = {};
    const width = 100 / fieldArray.length;
    visibleColumns.forEach((field) => {
      columnWidths[field] = `${width}%`;
    });

    return columnWidths;
  };

  if (containsAny(fieldArrayTypes, fieldArrayExpFieldsTypes)) {
    return (
      <>
        {fieldValues.fieldArray.map((field, index) => {
          const FieldComponent = fieldArrayViewComponents[field.type];

          return (
            <FieldComponent
              key={field.name}
              index={index}
              fieldValues={field}
              locale={locale}
              data={data[name]}
              fieldLabel={fieldLabel}
            />
          );
        })}
      </>
    );
  }

  return (
    <>
      <Label>{fieldLabel}</Label>
      <MultiColumnList
        interactive={false}
        contentData={fieldValue.length ? fieldValue : [{}]}
        visibleColumns={visibleColumns}
        columnMapping={getColumnMapping()}
        formatter={getFormatter()}
        columnWidths={getColumnWidths()}
        emptyMessage={<NoValue />}
      />
    </>
  );
}

FieldArrayView.propTypes = {
  fieldValues: PropTypes.object,
  locale: PropTypes.string,
  data: PropTypes.object,
};

export default FieldArrayView;
